import { type Node } from "@tiptap/core";
import TiptapImage, { type ImageOptions } from "@tiptap/extension-image";
import { Plugin } from "@tiptap/pm/state";
import { type NodeViewProps, ReactNodeViewRenderer } from "@tiptap/react";

import { supportedEditorImageTypes } from "commonComponents/btWrappers/editor/core/editor.utils";
import { type ImageRegistryState } from "commonComponents/btWrappers/editor/core/extensions/custom/image/image.types";
import { ImageNodeView } from "commonComponents/btWrappers/editor/core/extensions/custom/image/ImageNodeView";

export interface IBrowseBuildertrendFilesConfig {
    jobId: number;
    canViewPhotosPermission: boolean;
}

export interface IUploadData {
    downloadUrl: string;
    uploadMetadata: string;
}

export interface IDownloadData {
    blobUrl: string;
}

export type EditorImageUploadHandler = (
    file: Blob,
    onProgress: (progressPercent: number) => void,
) => Promise<IUploadData>;

export type EditorImageDownloadHandler = (
    downloadUrl: string,
    uploadMetadata: string,
    onProgress: (progressPercent: number) => void,
    abortSignal: AbortSignal,
) => Promise<IDownloadData>;

export interface IUploadConfig {
    onUploadStaticRef: EditorImageUploadHandler;
    onDownloadStaticRef: EditorImageDownloadHandler;
    isImageUploadDisabledByBrowser?: boolean;
}

export interface IImageStorage {
    /**
     * Registry keyed by a per-NodeView Symbol. The Symbol is stable across ProseMirror attribute
     * updates (which produce new Node instances), so one image = one entry for its whole life.
     */
    imageStates: Map<symbol, ImageRegistryState>;
}

export interface IImageOptions extends ImageOptions {
    uploadConfig?: IUploadConfig;
    browseBuildertrendFilesConfig?: IBrowseBuildertrendFilesConfig;
    /**
     * Callback to report image state changes to the extension consumer. Receives the whole registry map
     * Fires on registration, every state transition, DOM load, and NodeView destruction.
     * */
    onImageStateChange?: (imageStates: ReadonlyMap<symbol, ImageRegistryState>) => void;
}

export interface IImageAttributes {
    src: string;
    alt: string | undefined;
    title: string | undefined;
    textAlign: "left" | "center" | "right" | undefined;
    /** Natural image dimensions captured on first load. Used for placeholder sizing and as the
     *  default rendered size before the user resizes. Stored as a string to round-trip via
     *  `data-preferred-width`. */
    preferredWidth: string | undefined;
    /** See `preferredWidth`. */
    preferredHeight: string | undefined;
    /** Explicit width set by drag-resizing. Takes precedence over `preferredWidth` when present. */
    width: number | null;
    /** Explicit height set by drag-resizing. Takes precedence over `preferredHeight` when present. */
    height: number | null;
    uploadMetadata: string | undefined;
}

const ImageExtension = (TiptapImage as Node<IImageOptions, IImageStorage>).extend({
    addOptions() {
        return {
            ...this.parent?.(),
            HTMLAttributes: {},
            allowBase64: false,
            inline: false,
            onImageStateChange: undefined,
            resize: false,
        };
    },

    addStorage() {
        return {
            imageStates: new Map<symbol, ImageRegistryState>(),
        } satisfies IImageStorage;
    },

    onCreate() {
        // Notifies consumers after NodeView construction has populated the map. Required for
        // the case where no nodeviews are created and onImageStateChange would otherwise never fire
        this.options.onImageStateChange?.(this.storage.imageStates);
    },
    addAttributes() {
        return {
            ...this.parent?.(),
            // TipTap's Image extension keeps width/height in memory only — they're not written to
            // the saved HTML and not read back when the page loads. These overrides add the
            // save/load handling so the resized image height/width persist.
            width: {
                default: null,
                renderHTML(attributes: IImageAttributes) {
                    return attributes.width ? { width: attributes.width } : {};
                },
                parseHTML(element) {
                    const width = Number.parseInt(element.getAttribute("width") ?? "");
                    return Number.isNaN(width) ? null : width;
                },
            },
            height: {
                default: null,
                renderHTML(attributes: IImageAttributes) {
                    return attributes.height ? { height: attributes.height } : {};
                },
                parseHTML(element) {
                    const height = Number.parseInt(element.getAttribute("height") ?? "");
                    return Number.isNaN(height) ? null : height;
                },
            },
            preferredWidth: {
                default: undefined,
                renderHTML(attributes: IImageAttributes) {
                    return {
                        "data-preferred-width": attributes.preferredWidth ?? undefined,
                    };
                },
                parseHTML(element) {
                    const preferredWidthAsString = element.getAttribute("data-preferred-width");
                    return isNaN(parseInt(preferredWidthAsString ?? "null"))
                        ? undefined
                        : preferredWidthAsString;
                },
            },
            preferredHeight: {
                default: undefined,
                renderHTML(attributes: IImageAttributes) {
                    return {
                        "data-preferred-height": attributes.preferredHeight ?? undefined,
                    };
                },
                parseHTML(element) {
                    const preferredHeightAsString = element.getAttribute("data-preferred-height");
                    return isNaN(parseInt(preferredHeightAsString ?? "null"))
                        ? undefined
                        : preferredHeightAsString;
                },
            },
            uploadMetadata: {
                default: undefined,
                keepOnSplit: true,
                renderHTML: (attributes: IImageAttributes) => {
                    return attributes.uploadMetadata
                        ? {
                              "data-upload-metadata": attributes.uploadMetadata,
                          }
                        : null;
                },
                // Don't parse the upoad metadata. That way, copy and pasting an image will reupload it.
                // Not the best thing for storage costs, but it makes it so much easier to track image usages.
                // parseHTML(element) {
                //     return element.getAttribute("data-upload-metadata");
                // },
            },
        };
    },

    parseHTML() {
        return [
            {
                // Allow data-uri images
                tag: 'img[src^="data:"]',
                ignore: !this.options.allowBase64,
            },
            {
                // Allow blob images, but only if from the current domain
                tag: `img[src^="blob:${window.location.origin}"]`,
            },
            {
                // Allow https
                tag: 'img[src^="https:"]',
            },
        ];
    },

    addNodeView() {
        return (props) => {
            const storage = props.editor.storage[props.extension.name] as IImageStorage;
            const options = props.extension.options as IImageOptions;

            // One Symbol per NodeView, captured by closure so it survives attribute updates.
            const viewKey = Symbol("imageView");
            storage.imageStates.set(viewKey, "registered");
            options.onImageStateChange?.(storage.imageStates);

            // Wrapper passes viewKey through to ImageNodeView as a prop.
            const ImageNodeViewWithKey = (tiptapProps: NodeViewProps) => (
                <ImageNodeView {...tiptapProps} viewKey={viewKey} />
            );

            const reactNodeView = ReactNodeViewRenderer(ImageNodeViewWithKey)(props);

            // Clean up the entry when ProseMirror tears down this NodeView.
            const originalDestroy = reactNodeView.destroy?.bind(reactNodeView);
            reactNodeView.destroy = () => {
                storage.imageStates.delete(viewKey);
                options.onImageStateChange?.(storage.imageStates);
                originalDestroy?.();
            };

            return reactNodeView;
        };
    },

    addProseMirrorPlugins() {
        if (!this.options?.uploadConfig) {
            return this.parent?.() ?? [];
        }

        const extensionName = this.name;
        return [
            new Plugin({
                props: {
                    handlePaste(view, event) {
                        const items = Array.from(event.clipboardData?.items ?? []);
                        let clipboardContainsPastableImage = false;
                        if (event.clipboardData?.types.includes("text/html")) {
                            // If the clipboard contains HTML, let the default paste handler handle it
                            // You would think grabing the file straight from the clipboard is better, but unfortuantely
                            // gifs on Chrome do not get copied as GIFs, but rather as static PNGs :(
                            return false;
                        }
                        items.forEach((item) => {
                            const file = item.getAsFile();
                            if (file && supportedEditorImageTypes.mimeTypes.has(file.type)) {
                                clipboardContainsPastableImage = true;
                                const imgSrc = URL.createObjectURL(file);
                                const newImageNode = view.state.schema.nodes[extensionName].create({
                                    src: imgSrc,
                                });
                                const transaction =
                                    view.state.tr.replaceSelectionWith(newImageNode);
                                view.dispatch(transaction);
                            }
                        });
                        return clipboardContainsPastableImage;
                    },
                },
            }),
        ];
    },
});

export default ImageExtension;
